import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FolderIcon,
  FolderOpenIcon,
  XMarkIcon,
  StarIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Checkbox } from '../common/Checkbox';
import Modal from '../common/Modal';
import { Badge } from '../common/Badge';
import { pimService } from '../../services/pimService';
import type { Category } from '../../types/pim';

interface CategorySelectorProps {
  selectedCategoryIds: number[];
  primaryCategoryId?: number;
  onSelectionChange: (categoryIds: number[], primaryCategoryId?: number) => void;
  multiple?: boolean;
  label?: string;
  error?: string;
  help?: string;
  required?: boolean;
  disabled?: boolean;
}

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  selectedCategoryIds,
  primaryCategoryId,
  onSelectionChange,
  multiple = true,
  label = 'Kategorie',
  error,
  help,
  required = false,
  disabled = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [tempSelectedIds, setTempSelectedIds] = useState<number[]>(selectedCategoryIds);
  const [tempPrimaryId, setTempPrimaryId] = useState<number | undefined>(primaryCategoryId);

  // Fetch category tree
  const { data: categoryTreeData, isLoading, error: queryError } = useQuery({
    queryKey: ['pim-category-tree'],
    queryFn: async () => {
      try {
        return await pimService.getCategoryTree();
      } catch (error) {
        console.error('Failed to load categories:', error);
        throw error;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Auto-expand categories that have selected children when data loads
  useEffect(() => {
    if (categoryTreeData?.data && selectedCategoryIds.length > 0) {
      const newExpanded = new Set<number>();
      const findParentsOfSelected = (categories: Category[], parentId?: number) => {
        categories.forEach(category => {
          if (selectedCategoryIds.includes(category.id) && parentId) {
            newExpanded.add(parentId);
          }
          if (category.children?.length) {
            findParentsOfSelected(category.children, category.id);
          }
        });
      };
      findParentsOfSelected(categoryTreeData.data);
      setExpandedCategories(newExpanded);
    }
  }, [categoryTreeData, selectedCategoryIds]);

  // Update temp selection when props change
  useEffect(() => {
    setTempSelectedIds(selectedCategoryIds);
    setTempPrimaryId(primaryCategoryId);
  }, [selectedCategoryIds, primaryCategoryId]);

  const toggleExpanded = (categoryId: number) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleCategoryToggle = (categoryId: number) => {
    if (!multiple) {
      setTempSelectedIds([categoryId]);
      setTempPrimaryId(categoryId);
      return;
    }

    setTempSelectedIds(prev => {
      const newIds = prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId];
      
      // If removing the primary category, clear primary selection
      if (!newIds.includes(categoryId) && tempPrimaryId === categoryId) {
        setTempPrimaryId(undefined);
      }
      
      return newIds;
    });
  };

  const handlePrimaryChange = (categoryId: number) => {
    if (tempSelectedIds.includes(categoryId)) {
      setTempPrimaryId(tempPrimaryId === categoryId ? undefined : categoryId);
    }
  };

  const handleSave = () => {
    onSelectionChange(tempSelectedIds, tempPrimaryId);
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setTempSelectedIds(selectedCategoryIds);
    setTempPrimaryId(primaryCategoryId);
    setIsModalOpen(false);
  };

  const filterCategories = (categories: Category[], term: string): Category[] => {
    if (!term) return categories;
    
    return categories.filter(category => {
      const matchesSearch = category.name.toLowerCase().includes(term.toLowerCase()) ||
                           category.description?.toLowerCase().includes(term.toLowerCase());
      
      const hasMatchingChildren = category.children?.length && 
                                 filterCategories(category.children, term).length > 0;
      
      return matchesSearch || hasMatchingChildren;
    }).map(category => ({
      ...category,
      children: category.children ? filterCategories(category.children, term) : []
    }));
  };

  const renderCategoryTree = (categories: Category[], level = 0) => {
    const filteredCategories = filterCategories(categories, searchTerm);
    
    return filteredCategories.map((category) => (
      <div key={category.id} className="space-y-1">
        <div 
          className={`flex items-center justify-between p-2 rounded hover:bg-gray-50 ${
            level > 0 ? 'ml-' + (level * 4) : ''
          }`}
        >
          <div className="flex items-center space-x-2 flex-1">
            {category.children?.length ? (
              <button
                onClick={() => toggleExpanded(category.id)}
                className="p-1 hover:bg-gray-200 rounded"
              >
                <ChevronRightIcon 
                  className={`h-4 w-4 transform transition-transform ${
                    expandedCategories.has(category.id) ? 'rotate-90' : ''
                  }`} 
                />
              </button>
            ) : (
              <div className="w-6" />
            )}
            
            <Checkbox
              checked={tempSelectedIds.includes(category.id)}
              onChange={() => handleCategoryToggle(category.id)}
              disabled={disabled}
            />
            
            {category.children?.length ? (
              expandedCategories.has(category.id) ? 
                <FolderOpenIcon className="h-4 w-4 text-primary-600" /> :
                <FolderIcon className="h-4 w-4 text-gray-400" />
            ) : (
              <div className="h-4 w-4" />
            )}
            
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <span className={`text-sm ${
                  tempSelectedIds.includes(category.id) ? 'font-medium text-gray-900' : 'text-gray-700'
                }`}>
                  {category.name}
                </span>
                {tempPrimaryId === category.id && (
                  <Badge variant="primary" icon={StarIcon} size="sm">Główna</Badge>
                )}
                {!category.is_active && (
                  <Badge variant="secondary" size="sm">Nieaktywna</Badge>
                )}
              </div>
              {category.description && (
                <div className="text-xs text-gray-500 truncate">
                  {category.description}
                </div>
              )}
            </div>
          </div>

          {multiple && tempSelectedIds.includes(category.id) && (
            <button
              onClick={() => handlePrimaryChange(category.id)}
              className={`p-1 rounded hover:bg-gray-200 ${
                tempPrimaryId === category.id ? 'text-yellow-600' : 'text-gray-400'
              }`}
              title={tempPrimaryId === category.id ? 'Usuń jako główną' : 'Ustaw jako główną'}
            >
              <StarIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {category.children?.length && expandedCategories.has(category.id) && (
          <div className="ml-4">
            {renderCategoryTree(category.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  const getSelectedCategoryNames = () => {
    if (!categoryTreeData?.data || selectedCategoryIds.length === 0) {
      return [];
    }

    const findCategoryById = (categories: Category[], id: number): Category | null => {
      for (const category of categories) {
        if (category.id === id) return category;
        if (category.children) {
          const found = findCategoryById(category.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    return selectedCategoryIds.map(id => {
      const category = findCategoryById(categoryTreeData.data, id);
      return category ? {
        id,
        name: category.name,
        isPrimary: id === primaryCategoryId
      } : null;
    }).filter(Boolean);
  };

  const selectedNames = getSelectedCategoryNames();

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="space-y-2">
        {/* Selected Categories Display */}
        {selectedNames.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedNames.map((item: any) => (
              <div key={item.id} className="flex items-center">
                <Badge 
                  variant={item.isPrimary ? "primary" : "secondary"}
                  icon={item.isPrimary ? StarIcon : undefined}
                >
                  {item.name}
                </Badge>
                {!disabled && (
                  <button
                    onClick={() => onSelectionChange(
                      selectedCategoryIds.filter(id => id !== item.id),
                      primaryCategoryId === item.id ? undefined : primaryCategoryId
                    )}
                    className="ml-1 p-1 hover:bg-gray-200 rounded"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Selector Button */}
        <div>
          <Button
            type="button"
            variant="outline"
            onClick={(e) => {
              try {
                e.preventDefault();
                e.stopPropagation();
                setIsModalOpen(true);
              } catch (error) {
                console.error('Error opening category modal:', error);
              }
            }}
            disabled={disabled}
            icon={ChevronDownIcon}
            className="w-full justify-between"
          >
            <span>
              {selectedNames.length === 0 
                ? `Wybierz ${multiple ? 'kategorie' : 'kategorię'}...` 
                : `${selectedNames.length} ${multiple ? 'kategorii' : 'kategoria'} wybrane`
              }
            </span>
          </Button>
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
      
      {help && !error && (
        <p className="text-sm text-gray-500">{help}</p>
      )}

      {/* Selection Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCancel}
        title={`Wybierz ${multiple ? 'kategorie' : 'kategorię'}`}
        size="lg"
      >
        <div className="space-y-4">
          {/* Search */}
          <Input
            placeholder="Szukaj kategorii..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={undefined}
          />

          {multiple && (
            <div className="text-sm text-gray-600">
              <p className="font-medium">Instrukcje:</p>
              <ul className="list-disc list-inside space-y-1 mt-1">
                <li>Zaznacz kategorie, które chcesz przypisać do produktu</li>
                <li>Kliknij ikonę gwiazdki aby ustawić kategorię jako główną</li>
                <li>Główna kategoria będzie używana w URL i nawigacji</li>
              </ul>
            </div>
          )}

          {/* Category Tree */}
          <div className="border rounded-lg max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                <p className="mt-2 text-sm text-gray-600">Ładowanie kategorii...</p>
              </div>
            ) : queryError ? (
              <div className="text-center py-8 text-red-500">
                <ExclamationTriangleIcon className="mx-auto h-8 w-8 mb-2" />
                <p className="text-sm">Błąd podczas ładowania kategorii</p>
                <p className="text-xs text-gray-500 mt-1">{queryError instanceof Error ? queryError.message : 'Nieznany błąd'}</p>
              </div>
            ) : categoryTreeData?.data?.length ? (
              <div className="p-2">
                {renderCategoryTree(categoryTreeData.data)}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FolderIcon className="mx-auto h-8 w-8 mb-2" />
                <p>Brak dostępnych kategorii</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {tempSelectedIds.length > 0 && (
                <span>
                  Wybrane: {tempSelectedIds.length}
                  {tempPrimaryId && (
                    <span className="ml-2">
                      | Główna: {getSelectedCategoryNames().find((item: any) => item.id === tempPrimaryId)?.name}
                    </span>
                  )}
                </span>
              )}
            </div>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={handleCancel}
              >
                Anuluj
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
              >
                Zastosuj
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};